import type { Action, Reducer } from 'redux';
import CurrenciesService from '../service/currencies';
import { IApplicationState, IAppThunkAction } from '.';
import { ICurrency } from '../interfaces/ICurrency';
import { actionCreators as UIActions } from '../store/UI';
import { appInsights } from '../Logging/appInsights';

export interface ICurrencyState {
    currencies: Array<ICurrency>;
    isLoading: boolean;
    hasLoaded: boolean;
    loadFailed: boolean;
}

export const CURRENCIES_LOADING: 'CURRENCIES_LOADING' = 'CURRENCIES_LOADING';
export const CURRENCIES_LOADED: 'CURRENCIES_LOADED' = 'CURRENCIES_LOADED';
export const CURRENCIES_LOAD_FAILED: 'CURRENCIES_LOAD_FAILED' = 'CURRENCIES_LOAD_FAILED';

export interface ICurrenciesLoadingAction extends Action {
    type: typeof CURRENCIES_LOADING;
}

export interface ICurrenciesLoadedAction extends Action {
    type: typeof CURRENCIES_LOADED;
    currencies: Array<ICurrency>;
}

export interface ICurrenciesLoadFailedAction extends Action {
    type: typeof CURRENCIES_LOAD_FAILED;
}

export type KnownCurrencyActions =
    | ICurrenciesLoadingAction
    | ICurrenciesLoadedAction
    | ICurrenciesLoadFailedAction;

export const actionCreators = {
    loadGlobalPaymentCurrencies: (): IAppThunkAction<KnownCurrencyActions> => (dispatch: any, getState: any) => {
        const appState: IApplicationState = getState();
        if (appState && !appState.currencies.hasLoaded) {
            CurrenciesService.GetGlobalPaymentCurrencies()
                .then((data) => {
                    dispatch({ type: CURRENCIES_LOADED, currencies: data });
                })
                .catch((error: Error) => {
                    dispatch({ type: CURRENCIES_LOAD_FAILED });
                    dispatch(UIActions.displayError('errors.loadCurrenciesFailed'));
                    appInsights?.trackException(
                        { error: new Error(`Error loading global payment currencies`), severityLevel: 5 },
                        { exception: error, sourceFile: 'Currencies.ts', product: 'booking' }
                    );
                });

            dispatch({ type: CURRENCIES_LOADING });
        }
    },
};

const unloadedState: ICurrencyState = {
    currencies: [],
    isLoading: false,
    hasLoaded: false,
    loadFailed: false
};

export const reducer: Reducer<ICurrencyState> = (state: ICurrencyState | undefined, incomingAction: Action): ICurrencyState => {
    if (state === undefined) return unloadedState;

    const action: KnownCurrencyActions = incomingAction as KnownCurrencyActions;
    switch (action.type) {
        case CURRENCIES_LOADING:
            return {
                currencies: state.currencies,
                loadFailed: false,
                isLoading: true,
                hasLoaded: false,
            };
        case CURRENCIES_LOADED:
            return {
                currencies: action.currencies,
                loadFailed: false,
                isLoading: false,
                hasLoaded: true,
            };
        case CURRENCIES_LOAD_FAILED:
            return {
                ...state,
                loadFailed: true,
                isLoading: false,
                hasLoaded: false,
            };
        default:
            return state;
    }
};
